import * as React from 'react';
import { useMemo } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { IBackendCartEntries } from '@rbi-ctg/menu';
import { FavoriteCard } from 'components/favorite-card';
import {
  GetUserFavoritesRefsDocument,
  GetUserReorderFavoritesDocument,
  useDeleteFavoriteMutation,
  useGetUserReorderFavoritesQuery,
} from 'generated/rbi-graphql';
import { useDialogModal } from 'hooks/use-dialog-modal';
import { useAuthContext } from 'state/auth';
import { useLocationContext } from 'state/location';
import { useStoreContext } from 'state/store';
import { isCartEntryInMenu } from 'utils/cart';
import { editFavorite, routes } from 'utils/routing';
import { toast } from 'utils/toast';

import { LoggedOutEmptyState, NoFavoritesEmptyState } from './empty-state';
import { FavoritesCardListSkeleton } from './favorites-card-list.skeleton';
import { Wrapper } from './favorites-card-list.styled';
import { FavoriteCardData } from './types';

/**
 *
 * FavoritesCardList displays all the favorite orders and allows the guest to reorder, edit, and remove
 *
 */
const InnerFavoritesCardList = () => {
  const { data, loading } = useGetUserReorderFavoritesQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [deleteFavorite] = useDeleteFavoriteMutation({
    onCompleted: () => toast.success(formatMessage({ id: 'removeFavoriteSucess' })),
  });
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthContext();
  const { noStoreSelected } = useStoreContext();
  const { setStoreLocatorCallbackUrl } = useLocationContext();

  const [RemoveFavoriteModal, openRemoveFavoriteModal] = useDialogModal({
    onConfirm: ({ id }: { id: string }) => {
      deleteFavorite({
        variables: {
          favoriteId: id,
        },
        refetchQueries: [
          { query: GetUserReorderFavoritesDocument },
          { query: GetUserFavoritesRefsDocument },
        ],
      });
    },
    showCancel: true,
    modalAppearanceEventMessage: 'Remove Favorite',
  });

  const handleEditFavorite = (id: string) => {
    if (noStoreSelected) {
      setStoreLocatorCallbackUrl(`${routes.favorites}/${id}/edit`);
      navigate(routes.storeLocator);
    } else {
      editFavorite({ navigate, favoriteId: id });
    }
  };

  const handleRemoveFavorite = (id: string) => {
    openRemoveFavoriteModal({ id });
  };

  const favorites = data?.userFavorites?.favorites;
  const favoriteOrders = useMemo(() => {
    return favorites?.reduce((favsAccum: FavoriteCardData[], favorite) => {
      const entries = (favorite?.entries || ([] as unknown)) as IBackendCartEntries[];
      const filteredEntries = entries.filter(isCartEntryInMenu);

      if (filteredEntries.length) {
        favsAccum.push({
          id: favorite?._id || '',
          name: favorite?.name || '',
          entries: filteredEntries,
        });
      }

      return favsAccum;
    }, []);
  }, [favorites]);

  if (loading) {
    return <FavoritesCardListSkeleton />;
  }

  if (!favoriteOrders?.length) {
    if (!isAuthenticated()) {
      return <LoggedOutEmptyState />;
    } else {
      return <NoFavoritesEmptyState />;
    }
  }

  return (
    <>
      {favoriteOrders?.map((favorite, i) => {
        if (!favorite) {
          return null;
        }

        return (
          <FavoriteCard
            key={i}
            id={favorite.id}
            name={favorite.name}
            entries={favorite.entries}
            onEditFavorite={handleEditFavorite}
            onRemoveFavorite={handleRemoveFavorite}
          />
        );
      })}

      <RemoveFavoriteModal
        buttonLabel={formatMessage({ id: 'remove' })}
        heading={formatMessage({ id: 'removeFavorite' })}
        body={formatMessage({ id: 'removeFavoriteConfirm' })}
        showCloseButton
      />
    </>
  );
};

export const FavoritesCardList = () => (
  <Wrapper>
    <InnerFavoritesCardList />
  </Wrapper>
);
